/*
  Delivery estimate, rendered by snippets/product-delivery-estimate.liquid:
  "Delivery by Monday, 5 October. Order within 7 hrs 35 mins"
  "Delivering to Richmond 3121. To change, sign in or enter a postcode."

  Orders before the cut-off on a business day (Monday to Friday, not a closed date) ship that day,
  otherwise on the next business day. Delivery is the business days in transit for the shopper's
  postcode after dispatch (the block's postcode rules, else its days for other postcodes; without a
  postcode, its plain transit days). It's all worked out on the store's wall clock (its time zone,
  whatever the shopper's), and it refreshes every 30 seconds. More than a day before the cut-off it
  reads "Order by 2pm Monday" instead of a countdown. A delivery the next day reads "Tomorrow, 13 October", and when the
  cart's total plus the chosen variant's price times the quantity reaches the free shipping threshold
  (data-free-from, in cents) it reads "FREE delivery". The cart total starts from the page's Liquid
  and is re-read from /cart.js whenever the cart changes.

  On phones (under 768px) the wording is shortened so each part fits on one line:
  "Delivery by Mon, 5 Oct. Order within 7h 35m" and "To Richmond 3121. Sign in or change postcode."

  Signed-in customers use their primary saved address or an address selected for this session.
  Guests use their saved manual postcode, then an IP guess from the block's lookup URL.
  IP guesses are kept for a week; guesses inside Melbourne/Sydney's metro band show the CBD.
*/
if (!customElements.get('delivery-estimate')) {
  const DAY_MS = 86400000;
  const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  const STORAGE_KEY = 'mcs:delivery-location';
  const IP_GUESS_MAX_AGE = 7 * DAY_MS;
  const POSTCODE_LOOKUP = 'https://api.zippopotam.us/au/';

  const escapeHtml = (text) => String(text).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
  const isPostcode = (value) => /^\d{4}$/.test(String(value || '').trim());
  // An IP guess anywhere in these bands (inner metro, and the CBD PO-box postcodes) shows as the
  // CBD rather than a suburb that may well be wrong. Same delivery time either way.
  const IP_METRO_AREAS = [
    { ranges: [[3000, 3207], [8000, 8499]], postcode: '3000', city: 'Melbourne' },
    { ranges: [[2000, 2234], [1000, 1920]], postcode: '2000', city: 'Sydney' },
  ];
  const generaliseIpGuess = (location) => {
    if (!location) return location;
    const number = parseInt(location.postcode, 10);
    const area = IP_METRO_AREAS.find(({ ranges }) => ranges.some(([from, to]) => number >= from && number <= to));
    return area ? { ...location, postcode: area.postcode, city: area.city } : location;
  };
  // "RICHMOND" or "richmond" reads as "Richmond"; mixed case is left alone.
  const tidyPlace = (name) => {
    const text = String(name || '').trim();
    if (text !== text.toUpperCase() && text !== text.toLowerCase()) return text;
    return text.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
  };

  class DeliveryEstimate extends HTMLElement {
    connectedCallback() {
      this.closed = new Set((this.dataset.closed || '').split(/[\s,]+/).filter((date) => /^\d{4}-\d{2}-\d{2}$/.test(date)));
      const [hours, minutes] = (this.dataset.cutoff || '15:00').split(':').map((part) => parseInt(part, 10));
      this.cutoffHours = Number.isNaN(hours) ? 15 : hours;
      this.cutoffMinutes = Number.isNaN(minutes) ? 0 : minutes;
      this.transitDays = Math.max(0, parseInt(this.dataset.transitDays, 10) || 0);
      const regionalDays = parseInt(this.dataset.regionalDays, 10);
      this.regionalDays = Number.isNaN(regionalDays) ? this.transitDays : regionalDays;
      this.rules = this.parseRules(this.dataset.transitRules);

      // The purchase options (one-time / subscribe) report the quantity, which counts boxes for the bulk option
      this.onOptionsChange = (event) => {
        if (event.detail?.quantity) this.quantity = event.detail.quantity;
        this.render();
      };
      document.addEventListener('purchase-options:change', this.onOptionsChange);

      // Free delivery: the chosen variant's price times the quantity, re-checked when either changes
      // (the variant picker sets the form's id input, the theme's re-render publishes variantChange)
      try {
        this.prices = JSON.parse(this.querySelector('[data-variant-prices]')?.textContent || '{}');
      } catch (error) {
        this.prices = {};
      }
      // Out of stock (machine pages): "extra:2" adds business days, "soldout" hides the estimate (data-out-of-stock)
      try {
        this.stock = JSON.parse(this.querySelector('[data-variant-stock]')?.textContent || '{}');
      } catch (error) {
        this.stock = {};
      }
      this.outOfStock = this.dataset.outOfStock || '';
      // A sold-out variant (Shopify can't sell it) shows no estimate at all
      try {
        this.available = JSON.parse(this.querySelector('[data-variant-available]')?.textContent || '{}');
      } catch (error) {
        this.available = {};
      }
      this.freeFrom = parseInt(this.dataset.freeFrom, 10) || 0;
      this.cartTotal = parseInt(this.dataset.cartTotal, 10) || 0;
      this.onCartChange = () => this.refreshCart();
      this.cartUnsubscribe = window.theme?.pubsub?.subscribe?.(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, this.onCartChange);
      ['cart:updated', 'cart:refresh', 'bundle:added', 'bundle-builder:added'].forEach((name) => document.addEventListener(name, this.onCartChange));
      // Back from another page (bfcache) the cart may have changed
      this.onPageShow = (event) => { if (event.persisted) this.refreshCart(); };
      window.addEventListener('pageshow', this.onPageShow);
      this.onFormChange = (event) => {
        const input = event.target;
        if (!['id', 'quantity'].includes(input?.name)) return;
        // getAttribute: the form's own `id` property is shadowed by its input named "id"
        if ((input.getAttribute('form') || input.form?.getAttribute('id')) !== this.dataset.form) return;
        if (input.name === 'id') this.variantId = input.value;
        else this.quantity = null;
        this.render();
      };
      document.addEventListener('change', this.onFormChange);
      this.variantUnsubscribe = window.theme?.pubsub?.subscribe?.(theme.pubsub.PUB_SUB_EVENTS.variantChange, (event) => {
        if (!event?.data?.variant || !this.prices[event.data.variant.id]) return;
        this.variantId = String(event.data.variant.id);
        this.render();
      });

      this.signedIn = this.dataset.signedIn === 'true';
      this.bindAddressPicker();
      this.location = this.signedIn ? this.selectedAccountLocation()
        : this.savedLocation('manual') || generaliseIpGuess(this.savedLocation('ip'));
      this.bindPostcodeField();

      // Short wording on phones; re-rendered when the screen crosses the breakpoint
      this.phone = window.matchMedia?.('(max-width: 767px)');
      this.onPhoneChange = () => this.render();
      this.phone?.addEventListener('change', this.onPhoneChange);

      this.render();
      this.timer = setInterval(() => this.render(), 30000);
      if (!this.signedIn && !this.location) this.guessFromIp();
    }

    disconnectedCallback() {
      clearInterval(this.timer);
      this.phone?.removeEventListener('change', this.onPhoneChange);
      document.removeEventListener('purchase-options:change', this.onOptionsChange);
      document.removeEventListener('change', this.onFormChange);
      if (typeof this.variantUnsubscribe === 'function') this.variantUnsubscribe();
      if (typeof this.cartUnsubscribe === 'function') this.cartUnsubscribe();
      ['cart:updated', 'cart:refresh', 'bundle:added', 'bundle-builder:added'].forEach((name) => document.removeEventListener(name, this.onCartChange));
      window.removeEventListener('pageshow', this.onPageShow);
      clearTimeout(this.cartTimer);
      document.removeEventListener('delivery-address:change', this.onAddressChange);
      this.addressDialog?.close();
    }

    // "3000-3207, 8000-8499: 1" per line → [{ ranges: [[3000, 3207], [8000, 8499]], days: 1 }]
    parseRules(text) {
      return String(text || '').split(/\n+/).map((line) => {
        const at = line.lastIndexOf(':');
        if (at < 0) return null;
        const days = parseInt(line.slice(at + 1), 10);
        const ranges = line.slice(0, at).split(',').map((part) => {
          const [from, to] = part.split('-').map((n) => parseInt(n, 10));
          return Number.isNaN(from) ? null : [from, Number.isNaN(to) ? from : to];
        }).filter(Boolean);
        return Number.isNaN(days) || !ranges.length ? null : { ranges, days };
      }).filter(Boolean);
    }

    transitFor(postcode) {
      if (!isPostcode(postcode)) return this.transitDays;
      const number = parseInt(postcode, 10);
      const rule = this.rules.find(({ ranges }) => ranges.some(([from, to]) => number >= from && number <= to));
      return rule ? rule.days : this.regionalDays;
    }

    // Where we're delivering to

    savedLocation(source) {
      try {
        const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
        if (!saved || saved.source !== source || !isPostcode(saved.postcode)) return null;
        if (source === 'ip' && !(Date.now() - saved.at < IP_GUESS_MAX_AGE)) return null;
        return saved;
      } catch (error) {
        return null;
      }
    }

    saveLocation(location) {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...location, at: Date.now() }));
      } catch (error) {
        // Private browsing or storage full: it just isn't remembered.
      }
    }

    accountLocation() {
      const postcode = (this.dataset.accountPostcode || '').trim();
      return isPostcode(postcode) ? { postcode, city: this.dataset.accountCity || '', source: 'account' } : null;
    }

    get addressStorageKey() {
      return `mcs:delivery-address:${this.dataset.customerId}:${this.dataset.primaryAddressId}`;
    }

    addressLocation(id) {
      const option = this.addressOptions?.find((item) => item.dataset.addressId === id && !item.disabled);
      if (!option || !isPostcode(option.dataset.postcode)) return null;
      return { id, postcode: option.dataset.postcode, city: option.dataset.city, source: 'account' };
    }

    selectedAccountLocation() {
      let selected;
      try { selected = sessionStorage.getItem(this.addressStorageKey); } catch (_) { /* Storage is optional. */ }
      return this.addressLocation(selected) || this.addressLocation(this.dataset.primaryAddressId) || this.accountLocation();
    }

    bindAddressPicker() {
      this.addressDialog = this.querySelector('.delivery-estimate__address-dialog');
      this.addressOptions = [...this.querySelectorAll('[data-address-id]')];
      if (!this.signedIn || !this.addressDialog) return;
      const dialog = this.addressDialog;
      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-address-open]')) {
          this.addressOptions.forEach((option) => option.setAttribute('aria-pressed', String(option.dataset.addressId === this.location?.id)));
          dialog.showModal();
          // The dialog itself takes focus, not the close button, so the X doesn't open highlighted (on phones it
          // showed its focus ring after a tap). Tab still goes to the X first.
          dialog.focus({preventScroll: true});
        } else if (event.target.closest('[data-address-close]')) {
          dialog.close();
        } else {
          const option = event.target.closest('[data-address-id]');
          if (!option || option.disabled) return;
          const location = this.addressLocation(option.dataset.addressId);
          if (!location) return;
          this.location = location;
          try { sessionStorage.setItem(this.addressStorageKey, location.id); } catch (_) { /* Storage is optional. */ }
          document.dispatchEvent(new CustomEvent('delivery-address:change', {detail: {key: this.addressStorageKey, id: location.id}}));
          this.render();
          dialog.close();
        }
      });
      dialog.addEventListener('click', (event) => {
        const rect = dialog.getBoundingClientRect();
        if (event.target === dialog && (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom)) dialog.close();
      });
      dialog.addEventListener('close', () => this.querySelector('[data-address-open]')?.focus());
      this.onAddressChange = (event) => {
        if (event.detail.key !== this.addressStorageKey) return;
        const location = this.addressLocation(event.detail.id);
        if (location) { this.location = location; this.render(); }
      };
      document.addEventListener('delivery-address:change', this.onAddressChange);
    }

    // Expects JSON with city, postal and country_code (Workspace's /storefront/geo).
    guessFromIp() {
      const url = this.dataset.ipLookup;
      if (!url || this.ipLookupStarted) return;
      this.ipLookupStarted = true;
      this.fetchJson(url).then((data) => {
        const postcode = String(data?.postal || data?.postal_code || data?.zip || '').trim();
        const country = String(data?.country_code || data?.countryCode || data?.country || '').toUpperCase();
        if (this.location || country !== 'AU' || !isPostcode(postcode)) return;
        this.location = generaliseIpGuess({ postcode, city: tidyPlace(data.city), source: 'ip' });
        this.saveLocation(this.location);
        this.render();
      });
    }

    fetchJson(url) {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 4000);
      return fetch(url, { signal: controller.signal, credentials: 'omit' })
        .then((response) => (response.ok ? response.json() : null))
        .catch(() => null)
        .finally(() => clearTimeout(timeout));
    }

    // The postcode field

    get postcodeField() {
      return this.querySelector('.delivery-estimate__postcode');
    }

    bindPostcodeField() {
      const field = this.postcodeField;
      const input = field?.querySelector('input');
      if (!field || !input) return;

      this.addEventListener('click', (event) => {
        if (this.signedIn) return;
        if (event.target.closest('[data-change]')) {
          event.preventDefault();
          this.resetPostcodeField();
          field.hidden = false;
          input.value = this.location?.postcode || '';
          input.focus();
        } else if (event.target.closest('[data-cancel]')) {
          this.closePostcodeField();
        } else if (event.target.closest('[data-update]')) {
          this.submitPostcode();
        }
      });
      input.addEventListener('keydown', (event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          this.submitPostcode();
        } else if (event.key === 'Escape') {
          this.closePostcodeField();
        }
      });
      input.addEventListener('input', () => {
        field.querySelector('.delivery-estimate__error').hidden = true;
      });
    }

    // Back to asking for a postcode (not a suburb)
    resetPostcodeField() {
      const field = this.postcodeField;
      field.querySelector('input').hidden = false;
      field.querySelector('.delivery-estimate__suburb').hidden = true;
      field.querySelector('.delivery-estimate__error').hidden = true;
      this.pendingPostcode = null;
    }

    closePostcodeField() {
      this.postcodeField.hidden = true;
      this.resetPostcodeField();
      this.querySelector('[data-change]')?.focus();
    }

    saveManual(postcode, city) {
      this.location = { postcode, city, source: 'manual' };
      this.saveLocation(this.location);
      this.postcodeField.hidden = true;
      this.resetPostcodeField();
      this.render();
      this.querySelector('[data-change]')?.focus();
    }

    // The main suburb of a postcode is usually the one others are named after ("Cairns" before
    // "Cairns City", "Sydney" before "Sydney South"); otherwise the first alphabetically.
    likelySuburb(places) {
      return places.find((name) => places.some((other) => other !== name && other.startsWith(`${name} `))) || places[0];
    }

    async submitPostcode() {
      const field = this.postcodeField;
      const suburb = field.querySelector('.delivery-estimate__suburb');

      // Second step: the shopper picked one of the postcode's suburbs.
      if (this.pendingPostcode) {
        this.saveManual(this.pendingPostcode, suburb.value);
        return;
      }

      const input = field.querySelector('input');
      const update = field.querySelector('[data-update]');
      const postcode = input.value.trim();
      if (!isPostcode(postcode)) {
        field.querySelector('.delivery-estimate__error').hidden = false;
        input.focus();
        return;
      }

      update.disabled = true;
      const data = await this.fetchJson(`${POSTCODE_LOOKUP}${postcode}`);
      update.disabled = false;

      const places = [...new Set((data?.places || []).map((place) => tidyPlace(place['place name'])).filter(Boolean))]
        .sort((a, b) => a.localeCompare(b));
      if (places.length <= 1) {
        // One suburb, or the lookup didn't know it: save the postcode with what we have.
        this.saveManual(postcode, places[0] || '');
        return;
      }

      // A postcode covering several suburbs: ask which one, starting on the likeliest.
      const likely = this.likelySuburb(places);
      suburb.innerHTML = places.map((name) => `<option value="${escapeHtml(name)}"${name === likely ? ' selected' : ''}>${escapeHtml(name)} ${postcode}</option>`).join('');
      this.pendingPostcode = postcode;
      input.hidden = true;
      suburb.hidden = false;
      suburb.focus();
    }

    // The two lines

    // The store's wall-clock time now, as a UTC timestamp with the same fields, so the date maths
    // below never sees the shopper's time zone.
    storeNow() {
      try {
        const parts = {};
        new Intl.DateTimeFormat('en-US', {
          timeZone: this.dataset.timeZone || 'Australia/Melbourne',
          year: 'numeric', month: 'numeric', day: 'numeric',
          hour: 'numeric', minute: 'numeric', second: 'numeric', hourCycle: 'h23'
        }).formatToParts(new Date()).forEach(({ type, value }) => { parts[type] = parseInt(value, 10); });
        return Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour % 24, parts.minute, parts.second);
      } catch (error) {
        return null;
      }
    }

    // `day` is a store date at midnight (UTC fields).
    isBusinessDay(day) {
      const date = new Date(day);
      const weekday = date.getUTCDay();
      return weekday !== 0 && weekday !== 6 && !this.closed.has(date.toISOString().slice(0, 10));
    }

    nextBusinessDay(day) {
      let next = day + DAY_MS;
      for (let guard = 0; guard < 60 && !this.isBusinessDay(next); guard++) next += DAY_MS;
      return next;
    }

    render() {
      const text = this.querySelector('.delivery-estimate__text');
      const now = this.storeNow();
      if (now === null || !text) {
        this.hidden = true;
        return;
      }

      // Sold out: no estimate (any brand; Profitec and Fellow sell out at zero stock, see data-out-of-stock)
      const variantId = this.currentVariantId();
      const soldOut = this.outOfStock && variantId && this.stock[variantId] === false;
      if ((variantId && this.available[variantId] === false) || (soldOut && this.outOfStock === 'soldout')) {
        this.hidden = true;
        return;
      }

      if (this.signedIn && !this.location) {
        text.textContent = 'Select an Australian delivery address to see your delivery estimate.';
        this.renderWhere();
        this.hidden = false;
        return;
      }

      // The chosen variant out of stock but still on sale (Rocket): extra business days
      const extraDays = soldOut && this.outOfStock.startsWith('extra:') ? (parseInt(this.outOfStock.slice(6), 10) || 0) : 0;

      const today = now - (now % DAY_MS);
      const cutoffOffset = (this.cutoffHours * 60 + this.cutoffMinutes) * 60000;
      let dispatch = today;
      if (!this.isBusinessDay(today) || now >= today + cutoffOffset) dispatch = this.nextBusinessDay(today);

      let delivery = dispatch;
      const transit = this.transitFor(this.location?.postcode) + extraDays;
      for (let i = 0; i < transit; i++) delivery = this.nextBusinessDay(delivery);

      const remaining = dispatch + cutoffOffset - now;
      const order = remaining < DAY_MS
        ? `Order within <strong>${this.duration(remaining)}</strong>`
        : `Order by <strong>${this.cutoffLabel()} ${this.dayName(new Date(dispatch).getUTCDay())}</strong>`;

      // Multi-day transit is an upper-bound estimate; one-day service keeps
      // its exact next-business-day wording, even over weekends or holidays.
      const datePrefix = transit > 1 ? 'by ' : '';
      text.innerHTML = `${this.isFree() ? 'FREE delivery' : 'Delivery'} <strong>${datePrefix}${this.dateLabel(delivery, today)}</strong>. ${order}`;
      this.renderWhere();
      this.hidden = false;
    }

    currentVariantId() {
      const form = document.getElementById(this.dataset.form);
      return this.variantId || form?.querySelector('input[name="id"]')?.value || '';
    }

    // The cart's total plus the chosen variant's price times the quantity reaches the free shipping threshold
    isFree() {
      if (!this.freeFrom) return false;
      const form = document.getElementById(this.dataset.form);
      const id = this.variantId || form?.querySelector('input[name="id"]')?.value;
      const price = this.prices[id] || 0;
      const quantityInput = document.querySelector(`input[name="quantity"][form="${this.dataset.form}"]`)
        || form?.querySelector('input[name="quantity"]');
      const quantity = this.quantity || Math.max(1, parseInt(quantityInput?.value, 10) || 1);
      return this.cartTotal + price * quantity >= this.freeFrom;
    }

    // Several cart events can fire for one change; one /cart.js read a moment after the last
    refreshCart() {
      clearTimeout(this.cartTimer);
      this.cartTimer = setTimeout(async () => {
        try {
          const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, { headers: { Accept: 'application/json' } });
          const cart = await response.json();
          if (typeof cart.total_price === 'number' && cart.total_price !== this.cartTotal) {
            this.cartTotal = cart.total_price;
            this.render();
          }
        } catch (error) {
          // Offline or blocked: keep the last known total.
        }
      }, 300);
    }

    renderWhere() {
      const where = this.querySelector('.delivery-estimate__where');
      if (!where) return;

      const signedIn = this.dataset.signedIn === 'true';
      const signIn = this.dataset.loginUrl ? `<a class="delivery-estimate__link" href="${escapeHtml(this.dataset.loginUrl)}">sign in</a>` : '';
      const enter = (label) => `<button type="button" class="delivery-estimate__link" data-change>${label}</button>`;

      let html;
      if (signedIn) {
        const place = this.location ? [this.location.city, this.location.postcode].filter(Boolean).map(escapeHtml).join(' ') : 'Select a delivery address';
        const pin = this.querySelector('[data-pin-icon]')?.innerHTML.trim() || '';
        html = `${this.location ? 'Delivering to ' : ''}<button type="button" class="delivery-estimate__link" data-address-open aria-haspopup="dialog">${pin}${place}</button>${this.location ? '.' : ''}`;
      } else if (this.location) {
        const place = [this.location.city, this.location.postcode].filter(Boolean).map(escapeHtml).join(' ');
        if (this.phone?.matches) {
          const change = signedIn || !signIn
            ? enter('Change postcode')
            : `${signIn.replace('>sign in<', '>Sign in<')} or ${enter('change postcode')}.`;
          html = `To <strong>${place}</strong>. ${change}`;
        } else {
          const change = signedIn || !signIn
            ? enter('Change postcode')
            : `To change, ${signIn} or ${enter('enter a postcode')}.`;
          html = `Delivering to <strong>${place}</strong>. ${change}`;
        }
      } else {
        html = signedIn || !signIn
          ? `${enter('Enter a postcode')} for delivery to your area.`
          : `${signIn.replace('>sign in<', '>Sign in<')} or ${enter('enter a postcode')} for ${this.phone?.matches ? 'your area' : 'delivery to your area'}.`;
      }
      // Only when it changes, so the 30-second refresh doesn't take keyboard focus off its links.
      if (html !== this.whereHtml) {
        this.whereHtml = html;
        where.innerHTML = html;
      }
    }

    // "Monday, 5 October", or "Tomorrow, 5 October" the day after `today`
    dateLabel(day, today) {
      const date = new Date(day);
      const month = MONTHS[date.getUTCMonth()];
      const name = day - today === DAY_MS ? 'Tomorrow' : this.dayName(date.getUTCDay());
      return `${name}, ${date.getUTCDate()} ${this.phone?.matches ? month.slice(0, 3) : month}`;
    }

    // "Monday", or "Mon" on phones
    dayName(index) {
      return this.phone?.matches ? DAYS[index].slice(0, 3) : DAYS[index];
    }

    // "7 hrs 35 mins", "1 hr", "35 mins"; on phones "7h 35m"
    duration(ms) {
      const total = Math.max(1, Math.floor(ms / 60000));
      const hours = Math.floor(total / 60);
      const minutes = total % 60;
      const short = this.phone?.matches;
      const parts = [];
      if (hours) parts.push(short ? `${hours}h` : `${hours} ${hours === 1 ? 'hr' : 'hrs'}`);
      if (minutes) parts.push(short ? `${minutes}m` : `${minutes} ${minutes === 1 ? 'min' : 'mins'}`);
      return parts.join(' ');
    }

    // "2pm", "2:30pm"
    cutoffLabel() {
      const hour = this.cutoffHours % 12 || 12;
      const minutes = this.cutoffMinutes ? `:${String(this.cutoffMinutes).padStart(2, '0')}` : '';
      return `${hour}${minutes}${this.cutoffHours < 12 ? 'am' : 'pm'}`;
    }
  }

  customElements.define('delivery-estimate', DeliveryEstimate);
}
